/**
 * The default pilot brain: the phraseology the game has always used, expressed as phrase parts
 * behind the brain interface. It is deterministic, needs nothing outside the sim, and is the
 * fallback whenever another brain is slow, unavailable or says something the situation cannot
 * support. Wording comes from `phraseology.ts`, so at higher realism the same call is not always
 * made in exactly the same words.
 */
import { callsignPhrase, pilotPhrase, type RadioKind } from '../comms';
import { INTENTION_TEXT, NATURE_TEXT } from '../emergency';
import { patiencePhrase } from '../patience';
import { greetingKind, phraseParts, variantOf, type PhraseKind, type PhraseVars } from '../phraseology';
import { phraseInstruction, unablePhrase } from '../pilot';
import { clarificationFor, pilotAnswer } from '../pilot-brain';
import { renderParts, type PhrasePart } from './phrase';
import type { PilotBrain, PilotContext, PilotDecision, PilotIntent, PilotTrigger } from './types';

const words = (v: string): PhrasePart => ({ t: 'words', v });
const say = (parts: PhrasePart[], form: 'pilot' | 'plain', radio: RadioKind): PilotIntent => ({ kind: 'say', parts, form, radio });

/** A call that opens with the station and the callsign: "San Francisco Tower, UAL1, …". */
const call = (which: 'app' | 'twr' | 'dep', body: PhrasePart[]): PhrasePart[] => [{ t: 'station', which }, words(','), { t: 'callsign' }, words(','), ...body];

/**
 * Share of first calls that open with a greeting, scaled by the realism's variation. From the corpus
 * (docs/phraseology-research.md): about 9% of all pilot transmissions carry a greeting, and first
 * calls are a fraction of those, so roughly every other check-in greets.
 */
const GREETING_SHARE = 0.8;
/** Local colour is mid and high realism only; low keeps to the core wording. */
export const flavourOf = (ctx: Pick<PilotContext, 'realism'>): number => (ctx.realism.level === 'low' ? 0 : ctx.realism.phraseVariation);
const extrasFor = (ctx: Pick<PilotContext, 'realism' | 'airport'>, kind: PhraseKind) => (flavourOf(ctx) ? ctx.airport.xAtc.phraseExtras?.[kind] : undefined);

function intentsFor(trigger: PilotTrigger, ctx: PilotContext): PilotIntent[] {
  const { ac, airport } = ctx;
  const v = (kind: PhraseKind, vars: PhraseVars = {}) => phraseParts(kind, vars, ctx.rng, ctx.realism.phraseVariation, extrasFor(ctx, kind), ctx.ac.crewStyle);
  // First call to a station, mid and high realism: a time-of-day greeting now and then, and
  // "heavy" after a heavy's callsign (ICAO: on initial contact with tower and approach).
  const opening = (station: 'app' | 'twr', body: PhrasePart[]): PhrasePart[] => {
    const flavour = flavourOf(ctx);
    if (!flavour) return call(station, body);
    const heavy = ac.category === 'heavy' ? [words('heavy')] : [];
    const greet = ctx.localHour !== undefined && ctx.rng && ctx.rng.next() < flavour * GREETING_SHARE
      ? [...v(greetingKind(ctx.localHour)), words(',')] : [];
    return [{ t: 'station', which: station }, words(','), { t: 'callsign' }, ...heavy, words(','), ...greet, ...body];
  };
  const runwayPart = (name: string): PhrasePart => ({ t: 'runway', name });

  switch (trigger.kind) {
    case 'checkin': {
      if (ac.kind === 'arrival') {
        if (ac.control?.frequency === 'tower' && ac.approach) return [
          say(opening('twr', [words('established runway'), runwayPart(ac.approach.runway.name)]), 'plain', 'checkin'),
          { kind: 'request', request: 'landing' },
        ];
        return [
          say(opening('app', v('checkinArrival', { alt: { t: 'altitude', ft: ac.altitudeFt } })), 'plain', 'checkin'),
          { kind: 'request', request: 'contact' },
        ];
      }
      const rwy = runwayPart(ac.departureRunway?.name ?? '');
      const req = ac.requested ? [words(', requesting'), { t: 'fix' as const, name: ac.requested }] : [];
      return [say(opening('twr', v('checkinDeparture', { rwy, req })), 'plain', 'checkin'), { kind: 'request', request: 'departure' }];
    }
    case 'event':
      switch (trigger.event) {
        case 'clearOfRunway':
          return [say(v('clearOfRunway', { rwy: runwayPart(trigger.runway) }), 'pilot', 'report')];
        case 'established':
          return trigger.cleared
            ? [say(v('establishedLocalizer', { rwy: runwayPart(trigger.runway) }), 'pilot', 'report')]
            : [say(call('twr', v('establishedIls', { rwy: runwayPart(trigger.runway) })), 'plain', 'report')];
        case 'requestLanding':
          return [
            say(call('twr', v('shortFinal', { n: { t: 'number', n: 3 }, rwy: runwayPart(trigger.runway) })), 'plain', 'report'),
            { kind: 'request', request: 'landing' },
          ];
        case 'requestClimb':
          return [say(v('requestClimb'), 'pilot', 'report'), { kind: 'request', request: 'climb' }];
        case 'missedIntercept':
          return [say(v('missedIntercept'), 'pilot', 'report'), { kind: 'request', request: 'vectors' }];
        case 'goAround':
          return [say(v('goAround', { reason: words(trigger.reason) }), 'pilot', 'report')];
        case 'noApproachClearance':
          return [
            say(trigger.fix ? v('noApproachHold', { fix: { t: 'fix', name: trigger.fix } }) : v('noApproachVectors'), 'pilot', 'report'),
            { kind: 'request', request: 'vectors' },
          ];
        case 'clearanceLimit':
          return [say(v('clearanceLimitHold', { fix: { t: 'fix', name: trigger.fix } }), 'pilot', 'report'), { kind: 'request', request: 'approach' }];
        case 'endOfRoute': case 'offRoute':
          return [say(v(trigger.event, { fix: { t: 'fix', name: trigger.fix } }), 'pilot', 'report'), { kind: 'request', request: 'vectors' }];
      }
      break;
    case 'initiative':
      switch (trigger.want) {
        case 'holdingShort':
          return [
            say(call('twr', v('stillHoldingShort', { rwy: runwayPart(ac.departureRunway?.name ?? '') })), 'plain', 'report'),
            { kind: 'request', request: 'departure' },
          ];
        case 'approach':
          return [say(v('requestApproach', { rwy: runwayPart(ac.arrivalRunway?.name ?? airport.arrivalRunway) }), 'pilot', 'report'), { kind: 'request', request: 'approach' }];
        case 'descent':
          return [say(v('requestDescent'), 'pilot', 'report'), { kind: 'request', request: 'descent' }];
        case 'vectors':
          return [say(v('requestVectors'), 'pilot', 'report')];
        case 'sidClimb':
          return [say(v('requestSidClimb', { alt: { t: 'altitude', ft: trigger.altitudeFt } }), 'pilot', 'report'), { kind: 'request', request: 'climb' }];
      }
      break;
    case 'readback':
      return [{ kind: 'say', phrase: readback(ctx, trigger.instruction), radio: 'readback' }];
    case 'unable':
      return [{ kind: 'say', phrase: unablePhrase(ac, trigger.reason, ctx.telephony), radio: 'unable' }];
    case 'answer':
      return [{ kind: 'say', phrase: pilotPhrase(pilotAnswer(ac, trigger.subject, airport, ctx.realism), callsignPhrase(ac, ctx.telephony)), radio: 'report' }];
    case 'conversation': {
      const ask = clarificationFor(trigger.topic, trigger.text);
      if (ask) {
        const kind = ({ speed: 'clarifySpeed', altitude: 'clarifyAltitude', heading: 'clarifyHeading', instruction: 'clarifyInstruction' } as const)[ask];
        return [say(v(kind), 'pilot', 'report')];
      }
      const reply = trigger.topic === 'greeting' ? 'good day'
        : trigger.topic === 'capability' ? 'we will advise if unable'
          : trigger.topic === 'status' ? 'operating normally'
            : trigger.topic === 'reason' ? 'unable as previously advised'
              : trigger.topic === 'operational' ? 'roger'
                : 'say again with your request';
      return [say([words(reply)], 'pilot', 'report')];
    }
    case 'reply':
      return [say(v(trigger.to === 'standby' ? 'standby' : 'roger'), 'pilot', 'report')];
    case 'fuel': {
      const minutes: PhrasePart[] = [{ t: 'number', n: Math.max(0, Math.round(trigger.minutesLeft)) }, words('minutes of fuel remaining')];
      const station = ac.approach || ac.kind === 'departure' ? 'twr' : 'app';
      if (trigger.state === 'minimum') {
        // ICAO: a statement that no further delay can be accepted, not a distress call.
        return [say(call(station, [words('minimum fuel'), words(','), ...minutes]), 'plain', 'report'), { kind: 'request', request: 'landing' }];
      }
      if (trigger.state === 'mayday') {
        return [say([words('mayday, mayday, mayday'), words(','), { t: 'station', which: station }, words(','), { t: 'callsign' }, words(', fuel emergency,'), ...minutes,
          words(', request priority landing runway'), runwayPart(trigger.runway)], 'plain', 'emergency'), { kind: 'request', request: 'landing' }];
      }
      return [say([words('we have lost both engines, fuel exhausted, going down')], 'pilot', 'emergency')];
    }
    case 'emergency': {
      // ICAO Doc 9432 order: the distress or urgency signal three times, station, callsign,
      // nature of the emergency, intentions, then position and level.
      const signal = trigger.level === 'mayday' ? 'mayday, mayday, mayday' : 'pan pan, pan pan, pan pan';
      const station = ac.kind === 'departure' || ac.approach ? 'twr' : 'app';
      return [
        say([words(signal), words(','), { t: 'station', which: station }, words(','), { t: 'callsign' },
          words(`, ${NATURE_TEXT[trigger.nature]},`), words(INTENTION_TEXT[trigger.intention]), runwayPart(trigger.runway),
          words(', passing'), { t: 'altitude', ft: Math.round(ac.altitudeFt / 100) * 100 }], 'plain', 'emergency'),
        { kind: 'request', request: 'landing' },
      ];
    }
    case 'patience': {
      const req = ac.pilot.request;
      if (!req) return [{ kind: 'silence' }];
      const phrase = patiencePhrase(ac, req, trigger.stage, airport, ctx.telephony, trigger.holdFix);
      const intents: PilotIntent[] = [{ kind: 'say', phrase, radio: 'report' }];
      if (trigger.stage === 3) intents.push({ kind: 'act', action: req.kind === 'departure' || req.kind === 'lineUp' ? 'returnToStand' : 'hold', ...(trigger.holdFix ? { fix: trigger.holdFix } : {}) });
      return intents;
    }
  }
  return [{ kind: 'silence' }];
}

/** Small changes in word order, with every clearance value still rendered by typed parts. */
function readback(ctx: PilotContext, instruction: Extract<PilotTrigger, { kind: 'readback' }>['instruction']) {
  const farewell = instruction.kind === 'contact' ? variantOf('farewell', ctx.rng, flavourOf(ctx), extrasFor(ctx, 'farewell'), ctx.ac.crewStyle) : undefined;
  const standard = phraseInstruction(ctx.ac, instruction, ctx.airport, ctx.telephony, farewell).pilot;
  const chance = ctx.ac.crewStyle ? Math.min(1, ctx.realism.phraseVariation * 1.75) : ctx.realism.phraseVariation;
  if (!ctx.rng || ctx.rng.next() >= chance) return standard;
  const p = (v: string): PhrasePart => ({ t: 'words', v });
  let parts: PhrasePart[];
  switch (instruction.kind) {
    case 'heading':
      if (ctx.ac.onGround) return standard;
      parts = [p('heading'), { t: 'heading', deg: instruction.headingDeg }, p(instruction.turn === 'L' ? ', left turn' : instruction.turn === 'R' ? ', right turn' : ', turning')];
      break;
    case 'speed':
      if (ctx.ac.onGround) return standard;
      parts = [p('maintain'), { t: 'speed', kt: instruction.speedKt }];
      break;
    case 'land': case 'approach':
      parts = [p('runway'), { t: 'runway', name: instruction.runway }, p(instruction.kind === 'land' ? 'cleared to land' : 'cleared ILS approach')];
      break;
    case 'takeoff': case 'lineUp':
      parts = [p('runway'), { t: 'runway', name: ctx.ac.departureRunway?.name ?? '' }, p(instruction.kind === 'takeoff' ? 'cleared for takeoff' : 'line up and wait')];
      break;
    default: return standard;
  }
  return pilotPhrase(renderParts(parts, ctx), callsignPhrase(ctx.ac, ctx.telephony));
}

export function createRulesBrain(): PilotBrain {
  return {
    id: 'rules',
    decide(trigger: PilotTrigger, ctx: PilotContext): PilotDecision {
      return { intents: intentsFor(trigger, ctx), source: 'rules' };
    },
  };
}
